import {
  contactValue,
  leadContact,
  type ContactField,
  type LeadContactValues,
} from "@/domain/lead-contact";
import {
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
  type DocumentData,
} from "firebase/firestore";
import { paths } from "./paths";
import { asDate } from "./codec";
import { requireManageAccess } from "@/lib/access-control";
import { leadStatuses, type ActivityInput, type LeadActivity } from "@/domain/lead-activity";
import type { LeadStatus } from "@/domain/types";

export function parseLeadActivity(id: string, raw: DocumentData): LeadActivity {
  const date = (value: unknown) => {
    const parsed = asDate(value, new Date(NaN));
    if (!Number.isFinite(parsed.getTime())) throw new Error("Invalid Activity timestamp");
    return parsed;
  };
  const common = { id, createdAt: date(raw["createdAt"]) };
  const source = raw["source"];
  if (raw["type"] === "lifecycle" && source === "system") {
    const event = raw["event"];
    if (event === "discovery_started" || event === "discovery_result_created")
      return { ...common, type: "lifecycle", source, event };
    if (
      event === "contact_requested" &&
      (raw["requestedAction"] === "quote" || raw["requestedAction"] === "expert")
    )
      return {
        ...common,
        type: "lifecycle",
        source,
        event,
        requestedAction: raw["requestedAction"],
      };
  }
  const createdBy = raw["createdBy"];
  if (typeof createdBy !== "string" || !createdBy) throw new Error("Invalid Activity author");
  if (
    raw["type"] === "status_change" &&
    source === "system" &&
    leadStatuses.includes(raw["fromStatus"]) &&
    leadStatuses.includes(raw["toStatus"])
  )
    return {
      ...common,
      type: "status_change",
      source,
      createdBy,
      fromStatus: raw["fromStatus"],
      toStatus: raw["toStatus"],
    };
  const updated = raw["updatedAt"] ? { updatedAt: date(raw["updatedAt"]) } : {};
  if (raw["type"] === "note" && source === "user" && typeof raw["note"] === "string")
    return { ...common, ...updated, type: "note", source, createdBy, note: raw["note"] };
  if (
    raw["type"] === "follow_up" &&
    source === "user" &&
    ["pending", "completed", "cancelled"].includes(raw["status"])
  )
    return {
      ...common,
      ...updated,
      type: "follow_up",
      source,
      createdBy,
      scheduledAt: date(raw["scheduledAt"]),
      status: raw["status"],
      ...(typeof raw["note"] === "string" ? { note: raw["note"] } : {}),
    };
  throw new Error("Invalid Lead Activity");
}
export function subscribeLeadActivities(
  customerId: string,
  leadId: string,
  next: (activities: LeadActivity[]) => void,
  error: (error: unknown) => void,
) {
  return onSnapshot(
    query(paths.leadActivities(customerId, leadId), orderBy("createdAt", "desc")),
    (snapshot) => {
      try {
        next(
          snapshot.docs.map((doc) =>
            parseLeadActivity(doc.id, doc.data({ serverTimestamps: "estimate" })),
          ),
        );
      } catch (failure) {
        error(failure);
      }
    },
    error,
  );
}
export async function changeLeadStatus(
  customerId: string,
  leadId: string,
  toStatus: LeadStatus,
  activityId: string = crypto.randomUUID(),
) {
  if (!leadStatuses.includes(toStatus)) throw new Error("Invalid lead status");
  return runTransaction(paths.customer(customerId).firestore, async (tx) => {
    const { userId } = await requireManageAccess(customerId, tx);
    const leadRef = paths.lead(customerId, leadId);
    const activityRef = paths.leadActivity(customerId, leadId, activityId);
    const [lead, activity] = await Promise.all([tx.get(leadRef), tx.get(activityRef)]);
    if (!lead.exists()) throw new Error("Lead not found");
    if (activity.exists()) {
      if (
        activity.data()["type"] !== "status_change" ||
        activity.data()["toStatus"] !== toStatus ||
        activity.data()["createdBy"] !== userId
      )
        throw new Error("Activity ID already used");
      return;
    }
    const fromStatus = lead.data()["status"] as LeadStatus;
    if (!leadStatuses.includes(fromStatus)) throw new Error("Invalid saved status");
    if (fromStatus === toStatus) return;
    tx.update(leadRef, {
      status: toStatus,
      updatedAt: serverTimestamp(),
      statusActivityId: activityId,
    });
    tx.set(activityRef, {
      type: "status_change",
      source: "system",
      createdBy: userId,
      fromStatus,
      toStatus,
      createdAt: serverTimestamp(),
    });
  });
}
function payload(input: ActivityInput) {
  const note = input.note?.trim() ?? "";
  if (note.length > 5000 || (input.type === "note" && !note))
    throw new Error("Invalid Activity note");
  if (input.type === "note") return { type: input.type, note };
  if (!Number.isFinite(input.scheduledAt.getTime())) throw new Error("Invalid follow-up date");
  return { type: input.type, scheduledAt: Timestamp.fromDate(input.scheduledAt), note };
}
export async function createLeadActivity(
  customerId: string,
  leadId: string,
  input: ActivityInput,
  activityId: string = crypto.randomUUID(),
) {
  const data = payload(input);
  await runTransaction(paths.customer(customerId).firestore, async (tx) => {
    const { userId } = await requireManageAccess(customerId, tx);
    const ref = paths.leadActivity(customerId, leadId, activityId);
    const [lead, activity] = await Promise.all([
      tx.get(paths.lead(customerId, leadId)),
      tx.get(ref),
    ]);
    if (!lead.exists()) throw new Error("Lead not found");
    if (activity.exists()) {
      if (
        activity.data()["createdBy"] !== userId ||
        activity.data()["type"] !== input.type ||
        (activity.data()["note"] !== input.note?.trim() &&
          !(input.type === "follow_up" && !input.note && !activity.data()["note"])) ||
        (input.type === "follow_up" &&
          asDate(activity.data()["scheduledAt"]).getTime() !== input.scheduledAt.getTime())
      )
        throw new Error("Activity ID already used");
      return;
    }
    tx.set(ref, {
      ...data,
      source: "user",
      createdBy: userId,
      createdAt: serverTimestamp(),
      ...(input.type === "follow_up" ? { status: "pending" } : {}),
    });
  });
  return activityId;
}
export async function updateLeadActivity(
  customerId: string,
  leadId: string,
  id: string,
  change: ActivityInput | { status: "completed" | "cancelled" } | { delete: true },
) {
  await runTransaction(paths.customer(customerId).firestore, async (tx) => {
    await requireManageAccess(customerId, tx);
    const ref = paths.leadActivity(customerId, leadId, id);
    const saved = await tx.get(ref);
    if (!saved.exists()) throw new Error("Activity not found");
    const activity = parseLeadActivity(id, saved.data());
    if (activity.source !== "user") throw new Error("System Activities are immutable");
    if ("delete" in change) {
      if (activity.type !== "note") throw new Error("Cancel Follow-ups instead of deleting them");
      tx.delete(ref);
    } else if ("status" in change) {
      if (activity.type !== "follow_up" || activity.status !== "pending")
        throw new Error("Follow-up is no longer pending");
      tx.update(ref, { status: change.status, updatedAt: serverTimestamp() });
    } else {
      if (activity.type !== change.type) throw new Error("Activity type cannot change");
      tx.update(ref, { ...payload(change), updatedAt: serverTimestamp() });
    }
  });
}
// Called only inside the new-lead transaction, never during reads or rendering.
export function initialLeadActivities(raw: DocumentData, requestedAction: "quote" | "expert") {
  const date = (value: unknown) =>
    (value instanceof Date && Number.isFinite(value.getTime())) || value instanceof Timestamp
      ? value
      : null;
  const started = date(raw["startedAt"]);
  const completed = date(raw["completedAt"]) ?? date(raw["result"]?.generatedAt);
  return [
    ...(started
      ? [
          {
            id: "discovery-started",
            type: "lifecycle",
            source: "system",
            event: "discovery_started",
            createdAt: started,
          },
        ]
      : []),
    ...(completed
      ? [
          {
            id: "discovery-result",
            type: "lifecycle",
            source: "system",
            event: "discovery_result_created",
            createdAt: completed,
          },
        ]
      : []),
    {
      id: "contact-requested",
      type: "lifecycle",
      source: "system",
      event: "contact_requested",
      requestedAction,
      createdAt: serverTimestamp(),
    },
  ];
}

export async function createManualCustomerLead(
  customerId: string,
  input: LeadContactValues,
  id: string = crypto.randomUUID(),
) {
  const contact = leadContact(input);
  await runTransaction(paths.customer(customerId).firestore, async (tx) => {
    await requireManageAccess(customerId, tx);
    const ref = paths.lead(customerId, id);
    if ((await tx.get(ref)).exists()) throw new Error("Lead already exists");
    tx.set(ref, {
      leadOrigin: "manual",
      mode: "live",
      contact,
      status: "new",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });
  return id;
}
export async function editCustomerLeadContact(
  customerId: string,
  id: string,
  field: ContactField,
  input: string,
) {
  const value = contactValue(field, input),
    activityId = crypto.randomUUID();
  await runTransaction(paths.customer(customerId).firestore, async (tx) => {
    const { userId } = await requireManageAccess(customerId, tx);
    const ref = paths.lead(customerId, id),
      saved = await tx.get(ref);
    if (!saved.exists()) throw new Error("Lead not found");
    if ((saved.data()["contact"]?.[field] ?? "") === value) return;
    tx.update(ref, { [`contact.${field}`]: value, updatedAt: serverTimestamp() });
    tx.set(paths.leadActivity(customerId, id, activityId), {
      type: "note",
      source: "user",
      createdBy: userId,
      note: `Contact updated: ${field}`,
      createdAt: serverTimestamp(),
    });
  });
}
